import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { dialogOverlayClass, overlayCloseClass } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';

/**
 * Sheet — an edge-anchored modal panel. Same contract as Dialog (focus trap, Esc,
 * focus restore, inert background) but it slides rather than scales.
 *
 * The slide keyframes live in a hoisted <style> because base.css only carries the small
 * nudge variants; a full-travel slide is specific to this component.
 */

const SHEET_CSS = `
@keyframes af-sheet-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
@keyframes af-sheet-out-right { from { transform: translateX(0); } to { transform: translateX(100%); } }
@keyframes af-sheet-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }
@keyframes af-sheet-out-left { from { transform: translateX(0); } to { transform: translateX(-100%); } }
@keyframes af-sheet-in-bottom { from { transform: translateY(100%); } to { transform: translateY(0); } }
@keyframes af-sheet-out-bottom { from { transform: translateY(0); } to { transform: translateY(100%); } }
`;

export type SheetSide = 'right' | 'left' | 'bottom';

const SIDE_CLASS: Record<SheetSide, string> = {
  right: cn(
    'inset-y-0 right-0 h-full border-l',
    'data-[state=open]:animate-[af-sheet-in-right_var(--dur-normal)_var(--ease-out)]',
    'data-[state=closed]:animate-[af-sheet-out-right_var(--dur-fast)_var(--ease-in-out)]',
  ),
  left: cn(
    'inset-y-0 left-0 h-full border-r',
    'data-[state=open]:animate-[af-sheet-in-left_var(--dur-normal)_var(--ease-out)]',
    'data-[state=closed]:animate-[af-sheet-out-left_var(--dur-fast)_var(--ease-in-out)]',
  ),
  bottom: cn(
    'inset-x-0 bottom-0 w-full rounded-t-[var(--radius-xl)] border-t',
    'data-[state=open]:animate-[af-sheet-in-bottom_var(--dur-normal)_var(--ease-out)]',
    'data-[state=closed]:animate-[af-sheet-out-bottom_var(--dur-fast)_var(--ease-in-out)]',
  ),
};

export interface SheetProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: ReactElement;
  title: ReactNode;
  hideTitle?: boolean;
  description?: ReactNode;
  headerActions?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  side?: SheetSide;
  /** Panel width for `right`/`left`. Default `--w-detail` × 1.4. */
  width?: number | string;
  /** Panel height for `bottom`. */
  height?: number | string;
  hideClose?: boolean;
  bare?: boolean;
  className?: string;
  dismissible?: boolean;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export function Sheet({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  hideTitle = false,
  description,
  headerActions,
  footer,
  children,
  side = 'right',
  width,
  height,
  hideClose = false,
  bare = false,
  className,
  dismissible = true,
  onOpenAutoFocus,
  onCloseAutoFocus,
}: SheetProps) {
  const blockDismiss = (event: Event) => {
    if (!dismissible) event.preventDefault();
  };

  const sizeStyle =
    side === 'bottom'
      ? { height: height ?? 'min(70dvh, 640px)', maxHeight: '90dvh' }
      : { width: width ?? 'min(560px, calc(100vw - var(--space-12)))' };

  return (
    <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <style href="af-sheet" precedence="component">
        {SHEET_CSS}
      </style>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={dialogOverlayClass} />
        <DialogPrimitive.Content
          style={sizeStyle}
          className={cn(
            'fixed z-[var(--z-modal)] flex flex-col overflow-hidden',
            'border-line bg-[var(--surface-1)] shadow-[var(--shadow-4)] focus:outline-none',
            SIDE_CLASS[side],
            className,
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={blockDismiss}
          onPointerDownOutside={blockDismiss}
          onInteractOutside={blockDismiss}
        >
          <header
            className={cn(
              'flex shrink-0 items-start gap-3 border-b border-line-subtle px-5 pt-4',
              description ? 'pb-3' : 'pb-4',
            )}
          >
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title
                className={cn(
                  'text-lg font-semibold leading-tight tracking-[var(--tracking-tight)] text-primary',
                  hideTitle && 'sr-only',
                )}
              >
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-base leading-snug text-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {headerActions ? (
              <div className="flex shrink-0 items-center gap-1">{headerActions}</div>
            ) : null}
            {hideClose ? null : (
              <DialogPrimitive.Close asChild>
                <button type="button" aria-label="Close panel" className={overlayCloseClass}>
                  <X className="size-4" aria-hidden />
                </button>
              </DialogPrimitive.Close>
            )}
          </header>

          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain',
              bare ? '' : 'px-5 py-4',
            )}
          >
            {children}
          </div>

          {footer ? (
            <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-line-subtle bg-[var(--surface-2)] px-5 py-3">
              {footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
